import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Bell, Gift, Heart, Eye, Sparkles, Rocket, Lock, Check, Loader2, X, Megaphone, ExternalLink, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import ProfileDetailModal from '../components/ProfileDetailModal';
import { getNotifications, markNotificationsRead, respondPrivatePhotoRequest, startConversation } from '../services/api';

const iconFor = (type) => {
  switch (type) {
    case 'gift_received': return <Gift className="w-5 h-5 text-pink-500" />;
    case 'new_match': return <Heart className="w-5 h-5 text-red-500" />;
    case 'profile_view': return <Eye className="w-5 h-5 text-blue-500" />;
    case 'icebreaker': return <Sparkles className="w-5 h-5 text-yellow-500" />;
    case 'boost_active': return <Rocket className="w-5 h-5 text-purple-500" />;
    case 'private_photo_request': return <Lock className="w-5 h-5 text-gray-600" />;
    case 'private_photo_approved': return <Check className="w-5 h-5 text-green-500" />;
    case 'private_photo_declined': return <Lock className="w-5 h-5 text-red-500" />;
    case 'broadcast': return <Megaphone className="w-5 h-5 text-[#009E60]" />;
    case 'photo_removed_by_admin': return <Camera className="w-5 h-5 text-red-500" />;
    default: return <Bell className="w-5 h-5 text-gray-500" />;
  }
};

const formatDate = (iso) => {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch { return ''; }
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailUserId, setDetailUserId] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  const handleMessage = async (profile) => {
    if (!profile?.user_id) return;
    try {
      const res = await startConversation(profile.user_id);
      setDetailUserId(null);
      navigate(`/messages/${res.data.match_id}`);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402 || status === 403) {
        setDetailUserId(null);
        setUpgradePrompt({
          message: e.response.data?.detail ||
            'Premium members can message anyone. Free members must match first.'
        });
      } else {
        alert(e.response?.data?.detail || 'Failed to open conversation');
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await getNotifications(false, 100);
        setItems(r.data.items || []);
        if ((r.data.unread || 0) > 0) {
          await markNotificationsRead();
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const handleTap = (n) => {
    if (n.type === 'private_photo_request') return; // handled by inline buttons
    if (n.type === 'photo_removed_by_admin') { navigate('/photo-required'); return; }
    if (n.actor_id) setDetailUserId(n.actor_id);
    else if (n.type === 'gift_received') navigate('/gifts/received');
    else if (n.type === 'new_match') navigate('/matches');
  };

  const respondPrivate = async (n, approve) => {
    const requestId = n?.meta?.request_id;
    if (!requestId) return;
    // Optimistic UI: mark decided so buttons disappear
    setItems(prev => prev.map(item => item.notification_id === n.notification_id ? { ...item, _decided: approve ? 'approved' : 'declined' } : item));
    try {
      await respondPrivatePhotoRequest(requestId, approve);
    } catch {
      // Revert on failure
      setItems(prev => prev.map(item => item.notification_id === n.notification_id ? { ...item, _decided: undefined } : item));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="notifications-page">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100" data-testid="notif-back-btn">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Notifications</h1>
          </div>
          <Bell className="w-5 h-5 text-gray-500" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-3">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 text-[#009E60] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="notif-empty">
            <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto flex items-center justify-center mb-3">
              <Bell className="w-7 h-7 text-gray-400" />
            </div>
            <div className="text-gray-700 font-semibold">You're all caught up</div>
            <div className="text-sm text-gray-500 mt-1">Check back later for new activity.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(n => (
              <div
                key={n.notification_id}
                className={`w-full flex items-start gap-3 p-3 rounded-2xl border ${n.read ? 'bg-white border-gray-100' : 'bg-[#009E60]/5 border-[#009E60]/20'}`}
                data-testid={`notif-item-${n.notification_id}`}
              >
                <button
                  onClick={() => handleTap(n)}
                  className="flex items-start gap-3 flex-1 min-w-0 text-left"
                  data-testid={`notif-tap-${n.notification_id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    {iconFor(n.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-sm">{n.title}</div>
                    {n.body && <div className="text-sm text-gray-600 mt-0.5 line-clamp-3">{n.body}</div>}
                    {n.type === 'broadcast' && n.meta?.image_url && (
                      <img src={n.meta.image_url} alt="" className="mt-2 rounded-xl max-h-40 w-full object-cover" data-testid={`broadcast-image-${n.notification_id}`} />
                    )}
                    {n.type === 'broadcast' && n.meta?.link_url && (
                      <a
                        href={n.meta.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 mt-2 px-3 py-1.5 rounded-full bg-[#009E60] text-white text-xs font-semibold"
                        data-testid={`broadcast-link-${n.notification_id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> {n.meta.link_label || 'Learn more'}
                      </a>
                    )}
                    {n.type === 'broadcast' && n.meta?.credits_attached > 0 && (
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[11px] font-bold">
                        <Gift className="w-3 h-3" /> +{n.meta.credits_attached} free credits
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">{formatDate(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#009E60] mt-2" />}
                </button>
                {n.type === 'private_photo_request' && !n._decided && (
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => respondPrivate(n, true)}
                      className="px-3 py-1.5 rounded-full bg-[#009E60] text-white text-xs font-semibold flex items-center gap-1 hover:bg-[#007A4B]"
                      data-testid={`private-approve-${n.notification_id}`}
                    >
                      <Check className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button
                      onClick={() => respondPrivate(n, false)}
                      className="px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 text-xs font-semibold flex items-center gap-1 hover:bg-gray-50"
                      data-testid={`private-decline-${n.notification_id}`}
                    >
                      <X className="w-3.5 h-3.5" /> Decline
                    </button>
                  </div>
                )}
                {n._decided && (
                  <div className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${n._decided === 'approved' ? 'bg-[#009E60]/10 text-[#007A4B]' : 'bg-gray-100 text-gray-500'}`}>
                    {n._decided === 'approved' ? 'Approved' : 'Declined'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {detailUserId && (
        <ProfileDetailModal
          userId={detailUserId}
          onClose={() => setDetailUserId(null)}
          onMessage={(p) => handleMessage(p)}
          onBlocked={() => setDetailUserId(null)}
        />
      )}

      <AnimatePresence>
        {upgradePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={() => setUpgradePrompt(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 max-w-sm w-full text-center"
              data-testid="upgrade-prompt"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Message anyone with Premium
              </h3>
              <p className="text-gray-600 mb-5 text-sm">{upgradePrompt.message}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setUpgradePrompt(null)} className="flex-1 rounded-full">Maybe later</Button>
                <Button
                  onClick={() => { setUpgradePrompt(null); navigate('/pricing'); }}
                  className="flex-1 bg-[#FFCE00] hover:bg-yellow-500 text-gray-900 rounded-full font-semibold"
                >
                  Go Premium
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default NotificationsPage;
